import Modal from "common/Modal/Modal";
import React from "react";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useCancelTradeModalContent } from "features/trades/CancelTrade/CancelTradeModalContent";
import { useBulkUpdateTradesModal } from "@shared/features/trades/common/hooks";
import {
  BulkCloseTradesModalProps,
  BulkCloseTradesStep,
} from "@shared/features/trades/common/types";
import { useTheme } from "styled-components";
import InactiveAccountWarning from "features/trades/CancelTrade/InactiveAccountWarning";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";

const BulkCloseTradesModal = ({
  open,
  tradeIDs,
  closeModal,
  tradeIDToTradeData,
  onFinish,
}: BulkCloseTradesModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const appTheme = useTheme();
  const { sellCoins, cancelTradeModalContent, setSellCoins } =
    useCancelTradeModalContent(true);
  const {
    onOkPress,
    step,
    isLoading,
    onCloseModal,
    modalTitle,
    okText,
    cancelText,
    numInactiveAccounts,
  } = useBulkUpdateTradesModal({
    tradeIDs,
    tradeIDToTradeData,
    open,
    closeModal,
    sellCoins,
    setSellCoins,
    onFinish,
  });

  const getContent = () => {
    if (step === BulkCloseTradesStep.EXISTING_COINS)
      return cancelTradeModalContent;
    else if (step === BulkCloseTradesStep.CONFIRM_INACTIVE_ACCOUNT) {
      return (
        <InactiveAccountWarning
          numItems={tradeIDs?.length}
          numInvalidItems={numInactiveAccounts}
        />
      );
    } else if (step === BulkCloseTradesStep.CONFIRM)
      return (
        <LoadingSpinnerOverlay
          isLoading={isLoading}
          hideContentWhileLoading={false}
          wrap={true}
          backgroundColor={appTheme.colors.cardBgColor}
        >
          <Text>{t("confirm_edit.continue_question")}</Text>
        </LoadingSpinnerOverlay>
      );
  };

  return (
    <Modal
      onCancel={onCloseModal}
      open={open}
      title={modalTitle}
      onOk={onOkPress}
      okText={okText}
      cancelText={cancelText}
      okButtonProps={{
        loading: isLoading,
      }}
      cancelButtonProps={{ disabled: isLoading }}
      destroyOnHidden={true}
    >
      {getContent()}
    </Modal>
  );
};

export default React.memo(BulkCloseTradesModal);
